import React, { useEffect } from 'react';
import { InfoCircle, NavArrowDown } from 'iconoir-react';
import { IconoirCheckboxControl } from './IconoirCheckboxControl';
import type { ScreenQuestion } from '../../features/flow/types';
import {
  cleanQuestionText,
  effectiveQuestionHelpText,
  isPngFileName,
  isAllowedDocumentUploadFileName,
  normalizeFileAnswerValue,
  type FileRef,
} from '../../features/flow/utils';

function answerMatchesOption(raw: unknown, opt: string | number): boolean {
  return String(raw ?? '').trim() === String(opt).trim();
}

export type QuestionFieldProps = {
  q: ScreenQuestion;
  answers: Record<string, unknown>;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  cleanedScreenTitle: string;
  sessionScreenId?: string;
  actionBusy: boolean;
  /** Lock interactive controls (e.g. get_your_tax_quote after payment). */
  inputsLocked?: boolean;
};

export function QuestionField({
  q,
  answers,
  setAnswers,
  cleanedScreenTitle,
  sessionScreenId,
  actionBusy,
  inputsLocked = false,
}: QuestionFieldProps) {
  const value = answers[q.id];
  const helpText = effectiveQuestionHelpText(q);
  const locked = Boolean(inputsLocked);

  // For any `<select>`, if the current answer is missing or not among the available options,
  // auto-select the API's `current_value` (when valid) or the first option. This prevents a
  // stale default (e.g. calendar year 2026 from session init) from being submitted when the user
  // continues without interacting with the dropdown.
  useEffect(() => {
    if (locked) return;
    if (q.field_type !== 'select') return;
    const opts = q.options ?? [];
    if (opts.length === 0) return;
    const cur = value;
    const curStr = cur !== undefined && cur !== null ? String(cur).trim() : '';
    const isValidOption = curStr !== '' && opts.some((o) => String(o).trim() === curStr);
    if (isValidOption) return;
    // Prefer API current_value if it is among the options, otherwise take the first option.
    const cvStr =
      q.current_value !== undefined && q.current_value !== null
        ? String(q.current_value).trim()
        : '';
    const preferred =
      cvStr !== '' && opts.some((o) => String(o).trim() === cvStr) ? cvStr : String(opts[0]);
    setAnswers((prev) => ({ ...prev, [q.id]: preferred }));
  }, [locked, sessionScreenId, q.field_type, q.id, q.options, q.current_value, value, setAnswers]);

  const labelFor = (text: string) => cleanQuestionText(text, cleanedScreenTitle);
  const helpNode = helpText ? (
    <div className="flex items-start gap-2 text-sm text-[#525353]">
      <div className="mt-[4px]">
        <InfoCircle className="h-4 w-4 text-[#6B7280]" />
      </div>
      <span>{helpText}</span>
    </div>
  ) : null;

  switch (q.field_type) {
    case 'tax_summary_section':
      return (
        <div className="w-full py-2 border-b border-[#E2E8F0]">
          <div className="text-sm font-semibold text-[#131315]">{labelFor(q.question_text)}</div>
        </div>
      );
    case 'review_display':
    case 'tax_calculated':
    case 'tax_breakdown':
    case 'tax_total': {
      const display = value === null || value === undefined || value === '' ? '' : String(value);
      const isTotal = q.field_type === 'tax_total';
      return (
        <div
          className={[
            'w-full flex items-center justify-between py-2',
          ].join(' ')}
        >
          <span className={isTotal ? 'text-sm font-semibold text-[#131315]' : 'text-sm text-[#525353]'}>
            {labelFor(q.question_text)}
          </span>
          <span className={isTotal ? 'text-sm font-semibold text-[#131315]' : 'text-sm text-[#131315] font-medium'}>
            {display || '—'}
          </span>
        </div>
      );
    }
    case 'radio': {
      const opts = q.options ?? [];
      return (
        <div className="flex flex-col gap-3">
          {opts.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-[#131315] text-sm">
              <input
                type="radio"
                name={q.id}
                checked={answerMatchesOption(value, opt)}
                onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))}
                className="h-4 w-4 accent-[#07A2A0]"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }
    case 'checkbox': {
      const opts = q.options ?? [];
      const current: string[] = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className={`flex flex-col gap-3 ${locked ? 'opacity-80' : ''}`}>
          {opts.map((opt) => {
            const checked = current.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 text-[#131315] text-sm ${locked ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <IconoirCheckboxControl
                  checked={checked}
                  disabled={locked}
                  onChange={() => {
                    setAnswers((prev) => {
                      const raw = prev[q.id];
                      const cur: string[] = Array.isArray(raw) ? (raw as string[]) : [];
                      const next = checked ? cur.filter((x) => x !== opt) : [...cur, opt];
                      return { ...prev, [q.id]: next };
                    });
                  }}
                />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      );
    }
    case 'select': {
      const opts = q.options ?? [];
      const selectClasses =
        'w-full appearance-none rounded-lg border border-[#E2E8F0] px-4 py-2 pr-10 text-[#131315] bg-white ' +
        'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]';
      const rawSelect = value;
      const effectiveSelect =
        rawSelect !== undefined && rawSelect !== null && String(rawSelect).trim() !== ''
          ? rawSelect
          : q.current_value !== undefined && q.current_value !== null
            ? q.current_value
            : '';
      const selectedStr = String(effectiveSelect);
      const fallbackCurrentStr =
        q.current_value !== undefined && q.current_value !== null ? String(q.current_value) : '';

      return (
        <div className="flex flex-col gap-2">
          <div className="relative w-full">
            <select
              className={selectClasses}
              value={selectedStr}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
            >
              {opts.length > 0 ? (
                opts.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))
              ) : fallbackCurrentStr ? (
                <option value={fallbackCurrentStr}>{fallbackCurrentStr}</option>
              ) : (
                <option value="" disabled>
                  Select...
                </option>
              )}
            </select>

            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <NavArrowDown className="w-[16px] h-[16px] text-[#131315]" />
            </div>
          </div>
          {helpNode}
        </div>
      );
    }
    case 'date': {
      const dateClasses =
        'w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white ' +
        'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]';
      return (
        <div className="flex flex-col gap-2">
          <input
            className={dateClasses}
            type="date"
            disabled={locked}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          />
          {helpNode}
        </div>
      );
    }
    case 'number': {
      const numberClasses =
        'w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white ' +
        'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]';
      const numberDisplay = value === '' || value === null || value === undefined ? '' : String(value);
      return (
        <div className="flex flex-col gap-2">
          <input
            className={numberClasses}
            type="number"
            value={numberDisplay}
            onChange={(e) =>
              setAnswers((prev) => ({
                ...prev,
                [q.id]: e.target.value === '' ? '' : Number(e.target.value),
              }))
            }
          />
          {helpNode}
        </div>
      );
    }
    case 'currency': {
      const currencyInputClasses =
        'w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white ' +
        'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] pl-10';
      const currencyDisplay = value === '' || value === null || value === undefined ? '' : String(value);
      return (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
            <input
              className={currencyInputClasses}
              type="number"
              step="0.01"
              disabled={locked}
              value={currencyDisplay}
              onChange={(e) =>
                setAnswers((prev) => ({
                  ...prev,
                  [q.id]: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
            />
          </div>
          {helpNode}
        </div>
      );
    }
    case 'file': {
      const acceptUpload = (q.id === 'aeat_upload_files' || /upload|document|id|tax.?return/i.test(q.id))
        ? isAllowedDocumentUploadFileName
        : isPngFileName;
      const acceptAttr =
        q.id === 'aeat_upload_files' || /upload|document|id|tax.?return/i.test(q.id)
          ? 'application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg'
          : 'image/png,.png';
      const fileRefs = normalizeFileAnswerValue(value).filter((r) => acceptUpload(r.name));
      return (
        <div className="w-full">
          <label
            className={[
              'h-10 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-[#939393] text-[#131315] font-semibold bg-white hover:border-gray-400 transition-colors leading-none',
              actionBusy || locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            <span>📄 Upload file</span>
            <input
              className="hidden"
              type="file"
              accept={acceptAttr}
              disabled={actionBusy || locked}
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length === 0) return;
                const pickedRefs: FileRef[] = picked
                  .map((f) => ({ kind: 'file' as const, name: f.name }))
                  .filter((r) => r.name.trim() !== '' && acceptUpload(r.name));
                if (pickedRefs.length === 0) {
                  e.currentTarget.value = '';
                  return;
                }
                setAnswers((prev) => {
                  const curVal = prev[q.id];
                  const cur = normalizeFileAnswerValue(curVal);
                  return { ...prev, [q.id]: [...cur, ...pickedRefs] };
                });
                e.currentTarget.value = '';
              }}
            />
          </label>
          {helpNode}

          {fileRefs.length > 0 ? (
            <div className="mt-3 flex flex-col gap-3">
              {fileRefs.map((ref, idx) => (
                <div
                  key={`${ref.name}_${idx}`}
                  className="w-full max-w-[310px] rounded border border-[#E2E8F0] bg-[#F9FAFB] p-2 flex items-center justify-between"
                >
                  <span className="text-sm text-[#131315] break-all">
                    File {idx + 1}: {ref.name}
                  </span>
                  <button
                    type="button"
                    disabled={actionBusy || locked}
                    onClick={() =>
                      setAnswers((prev) => {
                        const curVal = prev[q.id];
                        const cur = normalizeFileAnswerValue(curVal);
                        const next = cur.filter((_, i) => i !== idx);
                        return { ...prev, [q.id]: next.length > 0 ? next : null };
                      })
                    }
                    className="text-sm font-semibold text-[#131315] disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      );
    }
    case 'email': {
      const emailInputClasses =
        'w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white ' +
        'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]';
      return (
        <div className="flex flex-col gap-2">
          <input
            className={emailInputClasses}
            type="email"
            disabled={locked}
            autoComplete="email"
            inputMode="email"
            placeholder={q.placeholder ?? ''}
            value={
              typeof value === 'string'
                ? value
                : value !== null && value !== undefined
                  ? String(value)
                  : ''
            }
            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          />
          {helpNode}
        </div>
      );
    }
    case 'text':
    default: {
      const isCoOwnershipPercentageText =
        sessionScreenId === 'co_ownership_details' && q.id.endsWith('_percentage');
      if (isCoOwnershipPercentageText) {
        const numberDisplay = value === '' || value === null || value === undefined ? '' : String(value);
        return (
          <div className="flex flex-col gap-2">
            <input
              className={
                'w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white ' +
                'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]'
              }
              type="number"
              disabled={locked}
              value={numberDisplay}
              onChange={(e) =>
                setAnswers((prev) => ({
                  ...prev,
                  [q.id]: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
            />
            {helpNode}
          </div>
        );
      }

      const textInputClasses =
        'w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white ' +
        'focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]';
      return (
        <div className="flex flex-col gap-2">
          <input
            className={textInputClasses}
            type="text"
            disabled={locked}
            placeholder={q.placeholder ?? ''}
            value={
              typeof value === 'string'
                ? value
                : value !== null && value !== undefined
                  ? String(value)
                  : ''
            }
            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
          />
          {helpNode}
        </div>
      );
    }
  }
}
